import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const HiringCostCalculator = () => {
  const [inputData, setInputData] = useState({
    expertSalary: '',
    expertTraining: '',
    expertOnboarding: '',
    expertBenefits: '',
    fresherSalary: '',
    fresherTraining: '',
    fresherOnboarding: '',
    fresherBenefits: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateHiringCost = () => {
    const {
      expertSalary,
      expertTraining,
      expertOnboarding,
      expertBenefits,
      fresherSalary,
      fresherTraining,
      fresherOnboarding,
      fresherBenefits
    } = inputData;

    // Validate input
    if (
      !expertSalary ||
      !expertTraining ||
      !expertOnboarding ||
      !expertBenefits ||
      !fresherSalary ||
      !fresherTraining ||
      !fresherOnboarding ||
      !fresherBenefits
    ) {
      setError('Please fill in all required fields');
      return;
    }

    if (
      isNaN(expertSalary) ||
      isNaN(expertTraining) ||
      isNaN(expertOnboarding) ||
      isNaN(expertBenefits) ||
      isNaN(fresherSalary) ||
      isNaN(fresherTraining) ||
      isNaN(fresherOnboarding) ||
      isNaN(fresherBenefits)
    ) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    const expertTotalCost = parseFloat(expertSalary) + parseFloat(expertTraining) + parseFloat(expertOnboarding) + parseFloat(expertBenefits);
    const fresherTotalCost = parseFloat(fresherSalary) + parseFloat(fresherTraining) + parseFloat(fresherOnboarding) + parseFloat(fresherBenefits);

    // Create visualization data
    const labels = ['Expert', 'Fresher'];
    const costs = [expertTotalCost, fresherTotalCost];

    const chartData = {
      labels,
      datasets: [
        {
          label: 'Hiring Cost',
          data: costs,
          backgroundColor: ['rgba(255, 99, 132, 0.5)', 'rgba(54, 162, 235, 0.5)'],
          borderColor: ['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)'],
          borderWidth: 1
        }
      ]
    };

    setResults({
      expertTotalCost,
      fresherTotalCost,
      chartData
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Cost of Hiring an Expert vs. Hiring a Fresher Calculator</title>
          <meta name="description" content="Compare the cost of hiring an expert versus a fresher. This calculator helps HR professionals and hiring managers make informed decisions." />
          <meta name="keywords" content="hiring cost calculator, expert vs fresher, HR tools" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">💼</div>
            <h1 className="text-3xl font-bold text-gray-800">Cost of Hiring an Expert vs. Hiring a Fresher Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Compare the cost of hiring an expert versus a fresher. This calculator helps HR professionals and hiring managers make informed decisions.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Expert Salary (per year):</label>
              <input
                type="text"
                name="expertSalary"
                value={inputData.expertSalary}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter expert salary"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Expert Training Cost:</label>
              <input
                type="text"
                name="expertTraining"
                value={inputData.expertTraining}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter expert training cost"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Expert Onboarding Cost:</label>
              <input
                type="text"
                name="expertOnboarding"
                value={inputData.expertOnboarding}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter expert onboarding cost"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Expert Benefits Cost:</label>
              <input
                type="text"
                name="expertBenefits"
                value={inputData.expertBenefits}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter expert benefits cost"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Fresher Salary (per year):</label>
              <input
                type="text"
                name="fresherSalary"
                value={inputData.fresherSalary}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter fresher salary"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Fresher Training Cost:</label>
              <input
                type="text"
                name="fresherTraining"
                value={inputData.fresherTraining}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter fresher training cost"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Fresher Onboarding Cost:</label>
              <input
                type="text"
                name="fresherOnboarding"
                value={inputData.fresherOnboarding}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter fresher onboarding cost"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Fresher Benefits Cost:</label>
              <input
                type="text"
                name="fresherBenefits"
                value={inputData.fresherBenefits}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter fresher benefits cost"
              />
            </div>
          </div>

          <button
            onClick={calculateHiringCost}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Hiring Cost
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Expert Hiring Cost</h3>
                  <p className="text-lg">Total Cost: ${results.expertTotalCost.toFixed(2)}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Fresher Hiring Cost</h3>
                  <p className="text-lg">Total Cost: ${results.fresherTotalCost.toFixed(2)}</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Cost ($)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Candidate Type'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Hiring Cost Comparison'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="text-gray-600">
            <p>Here are some frequently asked questions about the Hiring Cost Calculator:</p>
            <div className="mt-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What is the Hiring Cost Calculator?</strong>
                <p>The Hiring Cost Calculator is a tool that helps you compare the total cost of hiring an expert versus a fresher. It takes into account various costs such as salary, training, onboarding, and benefits.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: How does the calculator work?</strong>
                <p>The calculator sums up the costs for each candidate type (expert and fresher) and provides a comparison in a bar chart. It helps you visualize the total cost difference.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: Why should I use this calculator?</strong>
                <p>This calculator can help you make informed decisions by providing a clear comparison of the costs involved in hiring different types of candidates. It can also help in budget planning and resource allocation.</p>
              </div>
            </div>
          </div>
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Hiring Cost Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Costs for Expert:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Salary (per year)</li>
                  <li>Training Cost</li>
                  <li>Onboarding Cost</li>
                  <li>Benefits Cost</li>
                </ul>
              </li>
              <li><strong>Enter Costs for Fresher:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Salary (per year)</li>
                  <li>Training Cost</li>
                  <li>Onboarding Cost</li>
                  <li>Benefits Cost</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>Total hiring cost for an expert</li>
                  <li>Total hiring cost for a fresher</li>
                  <li>Visualization of the cost comparison</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the total estimated hiring costs for both candidate types</li>
                  <li>Use the visualization to better understand the cost differences</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Compare the total costs to make informed hiring decisions</li>
                <li>Consider the long-term benefits and potential ROI of hiring an expert vs. a fresher</li>
                <li>Regularly update the cost inputs to reflect current market conditions and company policies</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for hiring an expert versus a fresher:</p>
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Expert Salary: $80,000</li>
                <li>Expert Training Cost: $5,000</li>
                <li>Expert Onboarding Cost: $2,000</li>
                <li>Expert Benefits Cost: $10,000</li>
                <li>Fresher Salary: $40,000</li>
                <li>Fresher Training Cost: $3,000</li>
                <li>Fresher Onboarding Cost: $1,000</li>
                <li>Fresher Benefits Cost: $5,000</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Sum the costs for each candidate type</li>
                <li>Compare the total costs using the bar chart</li>
              </ol>

              <h4 className="mb-4">Results:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Expert Total Cost: $97,000</li>
                <li>Fresher Total Cost: $49,000</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default HiringCostCalculator;